import React, { Dispatch, RefObject, SetStateAction } from 'react';

import { content } from '@content';
import { MindsetData } from '@modules';
import { variables } from '@styles';
import { format, parse } from '@utils';

import {
  DATA_DISCLOSURE_ATTRIBUTE,
  DATA_EXPERIENCE_ID,
  DATA_FILLED_ATTRIBUTE,
  DATA_SPLITTER_ATTRIBUTE,
  DEFAULT_WRAPPED_ELEM_STYLES,
  ELEM_INFO_SVG,
  MODULE_INTERACTION_ACTIVE_ID,
  MODULE_INTERACTION_HOVER_ID,
  MODULE_INTERACTION_ACTIVE_ATTRIBUTE,
  MODULE_INTERACTION_HOVER_ATTRIBUTE,
  MODULE_INTERACTION_DRAG_ATTRIBUTE,
  ROUNDED_CORNER_HEIGHT,
  SAMPLE_TEMPLATE_IFRAME_ID,
  SPLITTER_ACTIVE_CLASSNAME,
  SPLITTER_DATASET_DISCLOSURE,
  SPLITTER_DATASET_EXPERIENCE_ID_KEY,
  SPLITTER_DATASET_ID_KEY,
  TRASH_SVG,
  TRASH_CAN_WIDTH,
} from './SampleTemplate.const';
import {
  CurrentTarget,
  DisclosureTitle,
  EmailTemplateSplitter,
  SplitterInfos,
  WrapperTagNames,
  HTMLElementWithDocument,
  HTMLWrappedElement,
  Rows,
  ModuleTemplateType,
} from './SampleTemplate.types';
import { delay } from '@utils/func/func';
import './SampleTemplate.styles.css';
import { MODULE_INTERACTION_ACTIVE_HEIGHT } from './ModuleModal/ModuleModal.const';

/**
 * SampleTemplate component utils
 */

// hide the passed overlay
export const resetIframeOverlay = (overlay: HTMLElement) => {
  overlay.style.display = 'none';
};

// getting the last splitter key in iframe
export const getLastSplitterKey = (iframe: RefObject<HTMLIFrameElement>) => {
  const frameSplittersArray = iframe.current?.contentWindow?.document.querySelectorAll(`[${DATA_SPLITTER_ATTRIBUTE}]`);
  return frameSplittersArray?.[frameSplittersArray.length - 1].getAttribute(DATA_SPLITTER_ATTRIBUTE);
};

// getting disclosure splitter
export const getDisclosureSplitter = (iframe: RefObject<HTMLIFrameElement>) => {
  return iframe.current?.contentWindow?.document.querySelector(`[${DATA_DISCLOSURE_ATTRIBUTE}]`);
};

export const handleSplitterDragOver = (e: DragEvent) => {
  e.preventDefault();
};

// functions for dragging of the Filled splitter
export const handleSplitterDragStart = (event: DragEvent) => {
  const target = event.target as EmailTemplateSplitter;
  const splitterId = target.dataset[SPLITTER_DATASET_ID_KEY] as string;

  // remove the overlay
  const activeOverlay = document.getElementById(MODULE_INTERACTION_ACTIVE_ID) as HTMLElement;
  if (activeOverlay) {
    resetIframeOverlay(activeOverlay);
  }

  if (target.dataset[SPLITTER_DATASET_ID_KEY] && !target.dataset[SPLITTER_DATASET_DISCLOSURE]) {
    const experienceId = parse.integer(target.dataset[SPLITTER_DATASET_EXPERIENCE_ID_KEY] ?? '');
    const { cursor, modules } = target.experienceData;

    event.dataTransfer?.setData(
      'application/json',
      JSON.stringify({
        cursor,
        experienceId,
        modules,
        sourceSplitterId: splitterId,
      }),
    );
    event.dataTransfer?.setData(`${splitterId}`, '');
  } else if (target.dataset[SPLITTER_DATASET_DISCLOSURE]) {
    event.dataTransfer?.setData(
      'application/json',
      JSON.stringify({
        disclosureHtml: target.innerHTML,
        sourceSplitterId: splitterId,
      }),
    );
    event.dataTransfer?.setData(`${splitterId}`, '');
  }
};

export const handleSplitterDragEnter = (event: DragEvent) => {
  const sourceSplitterId = event.dataTransfer?.types[1];
  const target = event.target as HTMLElement;

  if (target.dataset[SPLITTER_DATASET_ID_KEY] !== sourceSplitterId) {
    target.classList.add(SPLITTER_ACTIVE_CLASSNAME);
  }
};

export const handleSplitterDragLeave = (event: DragEvent) => {
  const target = event.target as HTMLElement;

  if (target.dataset[SPLITTER_DATASET_ID_KEY]) {
    target.classList.remove(SPLITTER_ACTIVE_CLASSNAME);
  }
};

export const handleSidebarDragStart = (event: DragEvent) => {
  let target = event.target as HTMLElement;

  // if the target is the image, we need to get its grandparent
  if (target.tagName === 'IMG') {
    target = (target.parentElement as HTMLElement).parentElement as HTMLElement;
  }
  target?.classList.add('dragging');

  // remove the overlay
  const activeOverlay = document.getElementById(MODULE_INTERACTION_ACTIVE_ID) as HTMLElement;
  if (activeOverlay) {
    resetIframeOverlay(activeOverlay);
  }

  const handleChangeDragClassName = (() => () => {
    target?.classList.remove('dragging');
    target?.classList.add('dragging-source');
  })();

  setTimeout(handleChangeDragClassName, 1);
};

export const handleSidebarDragEnd = (event: DragEvent) => {
  const target = event.target as HTMLElement;
  target?.classList.remove('dragging');
  target?.classList.remove('dragging-source');
};

const getLastStyles = (styles: string[][]) =>
  styles.reduce(
    (accum, current) => {
      if (current[0] && current[1]) {
        accum[current[0]] = current[1];
      }

      return accum;
    },
    {} as { [key: string]: string },
  );

const getSplitBackgroundImageStyles = (backgroundImageStyles: string[]) =>
  backgroundImageStyles.map((item) => {
    const splitStyles = item.trim().split(':');
    const styleName = splitStyles[0];
    const httpStr = splitStyles[1];
    const urlStr = splitStyles[2];

    if (splitStyles.length === 2) {
      return splitStyles;
    }

    return [styleName, [httpStr, urlStr].join(':')];
  });

export const getUniqueStyles = (styleString: string) => {
  // Split by ; and newline, trim, and filter out empty strings
  const styleItems = styleString
    .split(/;\s*\n?/)
    .map((item) => item.trim())
    .filter(Boolean);

  // Use a map to keep the last value for each property
  const styleMap: { [key: string]: string } = {};

  for (const item of styleItems) {
    // Split only on the first colon
    const idx = item.indexOf(':');
    if (idx === -1) continue;
    const key = item.slice(0, idx).trim();
    const value = item.slice(idx + 1).trim();
    styleMap[key] = value;
  }

  // Reconstruct the style string
  return (
    Object.entries(styleMap)
      .map(([key, value]) => `${key}: ${value}`)
      .join(';\n') + ';'
  );
};

// export const getUniqueStyles = (textContent: string) => {
//   const uniqueStyles = Array.from(new Set(textContent?.split(';').map((item) => item.trim()))).filter((item) => item);
//   const filteredUniqueStyles = uniqueStyles.filter((item) => !item.includes('background-image:'));
//   const backgroundImageStyles = uniqueStyles.filter((item) => item.includes('background-image:'));
//   const splitUniqueStyles = filteredUniqueStyles.map((item) => item.trim().split(':'));
//   const splitBackgrImageStyles = getSplitBackgroundImageStyles(backgroundImageStyles);
//   const lastStyles = getLastStyles([...splitUniqueStyles, ...splitBackgrImageStyles]);
//   const lastStylesString = Object.entries(lastStyles)
//     .map((item) => item.join(':'))
//     .join(';\n');

//   return `${lastStylesString};`;
// };

export const fixFrameScale = (frame: HTMLIFrameElement | null): number => {
  let scale = 1;

  const win = frame?.contentWindow;

  if (win) {
    const { documentElement: doc } = win.document;
    const { innerWidth: winWidth } = win;

    if (doc) {
      const { scrollWidth: docWidth, style: docStyle } = doc;
      if (docWidth > winWidth) {
        scale = winWidth / docWidth;

        docStyle.transformOrigin = 'left top';
        docStyle.transform = `scale(${scale})`;
      }
    }
  }

  return scale;
};

export const initializeElemStyleId = (contentDocument: Document, tagName: WrapperTagNames) => {
  const elems = contentDocument.querySelectorAll(tagName);

  Array.from(elems).forEach((elem, index) => {
    const currentTargetAttributes = elem?.attributes as HTMLWrappedElement;
    const textContent = currentTargetAttributes.style?.textContent;
    const styleItems = getUniqueStyles(textContent);

    elem.setAttribute('id', `${tagName}-${index}`);
    // Disabled adding styles directly to the element
    // elem.setAttribute('style', getUniqueStyles(`${styleItems} \n${DEFAULT_WRAPPED_ELEM_STYLES}`));
    elem.setAttribute('originalInnerHTML', elem.innerHTML.trim());
  });
};

export const initializeLinkStyleId = (contentDocument: Document) => {
  const links = contentDocument.querySelectorAll('a');
  let buttonCount = 0;

  Array.from(links).forEach((link, index) => {
    const linkAttributes = link?.attributes as HTMLWrappedElement;
    const textContent = linkAttributes.style?.textContent;
    const styleItems = getUniqueStyles(textContent);
    const hasBackgroundColor = Array.from(new Set(textContent?.split(';'))).some((item) =>
      item.includes('background-color'),
    );

    if (hasBackgroundColor) {
      link.setAttribute('id', `button-${index}`);
      // link.setAttribute('style', getUniqueStyles(`${styleItems} \n${DEFAULT_WRAPPED_ELEM_STYLES}`));
      buttonCount += 1;
    } else {
      link.setAttribute('id', `a-${index - buttonCount}`);
      // link.setAttribute('style', getUniqueStyles(`${styleItems} \n${DEFAULT_WRAPPED_ELEM_STYLES}`));
    }
  });
};

export const initializeDivStyleId = (contentDocument: Document) => {
  const textDivs = Array.from(contentDocument.querySelectorAll('div'));

  textDivs.forEach((textDiv, index) => {
    const linkAttributes = textDiv?.attributes as HTMLWrappedElement;
    const defaultStyles = linkAttributes.style?.textContent;

    // const styles = getUniqueStyles(
    //   `${defaultStyles} cursor: pointer; \nborder: .2rem solid transparent; \nborder-radius: 2px; \nborder-width: .2rem 0 !important;`,
    // );
    const styles = getUniqueStyles(defaultStyles);

    textDiv.setAttribute('id', `div-${index}`);
    textDiv.setAttribute('style', styles);
    textDiv.setAttribute(MODULE_INTERACTION_DRAG_ATTRIBUTE, 'true');
  });
};

export const initializeJourneyStyleId = (
  contentDocument: Document,
  positions: MindsetData[],
  chosenElemId: string | null,
) => {
  let nextChosenElemId: typeof chosenElemId = null;

  const divs = contentDocument.querySelectorAll('div');
  const moduleDivs = Array.from(divs).filter((div) => div.getAttribute(DATA_FILLED_ATTRIBUTE));

  moduleDivs.forEach((div, index) => {
    const experienceId = Number(div.getAttribute(DATA_FILLED_ATTRIBUTE));

    if (experienceId) {
      const experienceData = positions.find((item) => item.experienceId === experienceId);
      const modulesCount = (experienceData?.modules || []).length;

      if (modulesCount) {
        const type: ModuleTemplateType = modulesCount === 1 ? 'single' : 'group';
        const divId = `journey-${type}-div-${index}`;

        if (chosenElemId && div.getAttribute('id') === chosenElemId) {
          nextChosenElemId = divId;
        } else {
          // div.setAttribute(
          //   'style',
          //   `cursor: pointer; \nborder: ${variables.borderWidth.lg} solid transparent; \nborder-radius: ${variables.borderRadius.xxs}; \nborder-width: .2rem 0 !important; \nposition: relative;`,
          // );
        }

        div.setAttribute('id', divId);
        div.setAttribute(MODULE_INTERACTION_DRAG_ATTRIBUTE, 'true');
      }
    }
  });

  return nextChosenElemId;
};

export const removePrevElemInfo = (chosenElemId: string, contentDocument: Document) => {
  const splitChosenElemId = chosenElemId.split('-');

  if (splitChosenElemId.length === 2) {
    const prevElemTagName = splitChosenElemId[0];
    const prevElemIndex = splitChosenElemId[1];
    const prevElemInfo = contentDocument.getElementById(`elem-info-${prevElemTagName}-${prevElemIndex}`);
    // const prevTrashCan = contentDocument.getElementById(`trash-can-${prevElemTagName}-${prevElemIndex}`);

    if (prevElemInfo) prevElemInfo.remove();
    // if (prevTrashCan) prevTrashCan.remove();
  }
  if (splitChosenElemId.length === 4) {
    const prevElemTagName = `${splitChosenElemId[0]}-${splitChosenElemId[1]}-${splitChosenElemId[2]}`;
    const prevElemIndex = splitChosenElemId[3];
    const prevElemInfo = contentDocument.getElementById(`elem-info-${prevElemTagName}-${prevElemIndex}`);
    // const prevTrashCan = contentDocument.getElementById(`trash-can-${prevElemTagName}-${prevElemIndex}`);

    if (prevElemInfo) prevElemInfo.remove();
    // if (prevTrashCan) prevTrashCan.remove();
  }
};

export const convertElemInfoTagName = (elemId: string, currentTarget: CurrentTarget) => {
  let tagName = currentTarget?.tagName;

  if (elemId.includes('button')) {
    tagName = content.button;
  }

  if (elemId.includes('journey-single-div')) {
    tagName = content.singleModule;
  }

  if (elemId.includes('journey-group-div')) {
    tagName = content.moduleGroup;
  }

  switch (tagName) {
    case 'DIV':
      return content.div;
    case 'SPAN':
    case 'P':
    case 'H1':
    case 'STRONG':
    case 'A':
      return content.text;
    default:
      return format.capitalize(tagName);
  }
};

// Function for making splitters order array
export const getSplittersOrderArray = (splittersArray: EmailTemplateSplitter[]): SplitterInfos =>
  splittersArray.map((filledSplitter, index) => ({
    splitterId: filledSplitter.getAttribute(DATA_SPLITTER_ATTRIBUTE),
    experienceId: filledSplitter.getAttribute(DATA_FILLED_ATTRIBUTE),
    order: index + 1,
  }));

// Function for making splitters positions array
export const getSplittersPositionsArray = (splittersArray: EmailTemplateSplitter[]): SplitterInfos =>
  splittersArray
    .map((splitter, index) => ({
      splitterId: splitter.getAttribute(DATA_SPLITTER_ATTRIBUTE),
      experienceId: splitter.getAttribute(DATA_FILLED_ATTRIBUTE),
      order: index + 1,
    }))
    .filter((splitter) => splitter.experienceId);

// Function for sorting disclosures html sections
export const sortDisclosures = (infos: SplitterInfos, arrayToSort: HTMLElement[]) =>
  arrayToSort.sort((a, b) => {
    const experienceIdA = a.getAttribute(DATA_EXPERIENCE_ID);
    const experienceIdB = b.getAttribute(DATA_EXPERIENCE_ID);
    const orderA = infos.find((info) => info.experienceId === experienceIdA)?.order;
    const orderB = infos.find((info) => info.experienceId === experienceIdB)?.order;

    if (orderA && orderB && orderA !== orderB) {
      return orderA > orderB ? 1 : -1;
    }

    return 0;
  });

// Function for sorting disclosures titles
export const sortDisclosuresTitles = (infos: SplitterInfos, arrayToSort: DisclosureTitle[]) =>
  arrayToSort.sort((a, b) => {
    const experienceIdA = a.experienceId.toString();
    const experienceIdB = b.experienceId.toString();
    const orderA = infos.find((info) => info.experienceId === experienceIdA)?.order;
    const orderB = infos.find((info) => info.experienceId === experienceIdB)?.order;

    if (orderA && orderB && orderA !== orderB) {
      return orderA > orderB ? 1 : -1;
    }

    return 0;
  });

export const emptySplitter = (splitter: EmailTemplateSplitter) => {
  [DATA_FILLED_ATTRIBUTE, MODULE_INTERACTION_DRAG_ATTRIBUTE, 'class', 'id', 'style'].forEach((attr) =>
    splitter.removeAttribute(attr),
  );
  splitter.innerHTML = '';
  splitter.experienceData = {
    cursor: 0,
    modules: [],
  };
  splitter.disclosuresData = [];
};

export const addElemInfo = (target: any, elemId: string, documentElemId: string) => {
  //setting elemInfo
  const { contentDocument } = document.getElementById(documentElemId) as HTMLElementWithDocument;
  const elemInfo = contentDocument.createElement('div');
  const curElem = contentDocument.getElementById(elemId);

  elemInfo.setAttribute('id', `elem-info-${elemId}`);
  elemInfo.setAttribute('style', 'position: absolute; z-index: 1;');
  elemInfo.innerHTML = `
    <div
      id="elem-info-${elemId}__content"
      style="max-height: 18px; height: 18px; font-family: 'Manrope', sans-serif; padding: 1px 6px; border-radius: 0 0 2px 2px; display: flex; align-items: center; color: ${
        variables.color.primary.white
      }; background-color: ${variables.colorV2.module.border.active};margin-top: -2px;"
    >
      <span style="margin-right: 4px; font-size: 12px;">
        ${convertElemInfoTagName(elemId, target)}
      </span>
      ${ELEM_INFO_SVG}
    </div>
  `;

  curElem?.after(elemInfo);
};

export const addTrashCan = (target: any, elemId: string, documentElemId: string, curElemWidth: number) => {
  const { contentDocument } = document.getElementById(documentElemId) as HTMLElementWithDocument;
  const trashCan = contentDocument.createElement('div');

  trashCan.setAttribute('id', `trash-can-${elemId}`);

  // Set the style of the trashCan element based on the SVG width
  const trashCanStyles = `
    margin-left: ${curElemWidth}px; 
    `;
  trashCan.setAttribute('style', trashCanStyles);
  trashCan.classList.add('trashCanBtn');

  const innerTrashCan = document.createElement('div');
  innerTrashCan.setAttribute('id', `trash-can-${elemId}__content`);

  const innerTrashCanStyles = `
    align-items: center; 
    background-color: ${variables.colorV2.module.border.active};
    border-radius: .4rem; 
    bottom: 0;
    color: ${variables.color.primary.white}; 
    display: flex; 
    font-family: 'Manrope', sans-serif; 
    padding: 3px 5px; 
    position: absolute;
    right: -2.5rem; 
    `;
  innerTrashCan.setAttribute('style', innerTrashCanStyles);
  innerTrashCan.innerHTML = TRASH_SVG;
  trashCan.appendChild(innerTrashCan);

  target?.after(trashCan);
};

export const resetPrevElem = (
  elemId: string,
  documentElemId: string,
  previousChosenElemId: string,
  setChosenElemId: (value: SetStateAction<string>) => void,
) => {
  //reseting previous element
  const { contentDocument } = document.getElementById(documentElemId) as HTMLElementWithDocument;
  const prevChosenElem = contentDocument.getElementById(previousChosenElemId);
  const chosenElem = contentDocument.getElementById(elemId);

  if (elemId !== previousChosenElemId) {
    setChosenElemId(elemId);

    const prevElemStyles = prevChosenElem?.getAttribute('style') || '';
    const splitChosenElemStyles = prevElemStyles?.split(';').map((item) => item.replace('\n', ''));
    // const filteredStyles = `\n${splitedChosenElemStyles.excludeStrs(['border:', 'border-color:']).join(';\n')}`;
    const filteredStyles = splitChosenElemStyles.join(';\n');
    // const combinedStyles = getUniqueStyles(`${filteredStyles} \n${DEFAULT_WRAPPED_ELEM_STYLES}`).replaceAll(
    //   '!important',
    //   '',
    // );
    const combinedStyles = getUniqueStyles(filteredStyles);

    prevChosenElem?.setAttribute('style', combinedStyles);

    //removing prev elemInfo element
    // removePrevElemInfo(chosenElemId, contentDocument);
  }
};

export const restyleElemInfo = async (
  target: any,
  elemId: string,
  documentElemId: string,
  isTemplateView: boolean,
  setShowRemoveExperienceModal?: (value: SetStateAction<boolean>) => void,
) => {
  await delay(50);

  const { contentDocument } = document.getElementById(documentElemId) as HTMLElementWithDocument;
  //restyle elemInfo
  let elemInfoContent = contentDocument.getElementById(`elem-info-${elemId}__content`);

  if (!elemInfoContent && isTemplateView) {
    // addElemInfo(target, elemId, documentElemId);

    // await delay(50);

    elemInfoContent = contentDocument.getElementById(`elem-info-${elemId}__content`);
  }

  elemInfoContent?.setAttribute(
    'style',
    `max-height: 18px; \nheight: 18px; \nfont-family: 'Manrope', sans-serif; \npadding: 1px 6px; \nborder-radius: .4rem; \ndisplay: flex; \nalign-items: center; \ncolor: ${variables.color.primary.white}; \nbackground-color: ${variables.colorV2.module.border.active};margin-top: -2px;`,
  );

  //restyle trashCanContent
  // let trashCanContent = contentDocument.getElementById(`trash-can-${elemId}__content`);

  // if (!trashCanContent && setShowRemoveExperienceModal) {
  //   const curElemWidth = (target?.getBoundingClientRect().width ?? 1) - 1;

  //   // addTrashCan(target, elemId, documentElemId, curElemWidth);

  //   await delay(50);

  //   trashCanContent = contentDocument.getElementById(`trash-can-${elemId}__content`);
  // }

  // reset trash can x position

  // if (trashCanContent && setShowRemoveExperienceModal) {
  //   trashCanContent.setAttribute(
  //     'style',
  //     `font-family: 'Manrope', sans-serif;
  //      position: absolute;
  //      bottom: 0;
  //      right: -2.5rem;
  //      padding: 3px 5px;
  //      border-radius: .4rem;
  //      display: flex;
  //      align-items: center;
  //      color: ${variables.color.primary.white};
  //      background-color: ${variables.colorV2.module.border.active};
  //      cursor: pointer;`,
  //   );

  // trashCanContent.onclick = () => {
  //   setShowRemoveExperienceModal(true);
  // };
  // }
};

export const removeIframeOverlays = (iframe: HTMLIFrameElement) => {
  // remove overlays from iframe
  const frameDoc = iframe.contentDocument;
  const frameBody = frameDoc?.body;

  if (frameBody) {
    const overlays = frameDoc?.querySelectorAll<HTMLElement>(
      `#${MODULE_INTERACTION_HOVER_ID}, #${MODULE_INTERACTION_ACTIVE_ID}`,
    );

    if (overlays) {
      for (const overlay of overlays) {
        overlay.remove();
      }
    }
  }
};

// attach iframe element hover and active overlays
export const attachIframeOverlays = (iframe: HTMLIFrameElement) => {
  // attach overlays to iframe if they do not exist
  const frameDoc = iframe.contentDocument;
  const frameBody = frameDoc?.body;

  if (frameBody) {
    removeIframeOverlays(iframe);

    // Attach the hover and active state overlays
    const hoverDiv = document.createElement('div');
    hoverDiv.id = MODULE_INTERACTION_HOVER_ID;
    frameBody.append(hoverDiv);

    const activeDiv = document.createElement('div');
    activeDiv.id = MODULE_INTERACTION_ACTIVE_ID;
    frameBody.append(activeDiv);
  }
};

export const positionIframeOverlay = (overlay: HTMLElement, target: HTMLElement) => {
  const rect = target.getBoundingClientRect();

  const iframe = document.getElementById(SAMPLE_TEMPLATE_IFRAME_ID) as HTMLIFrameElement;
  const iframeRect = iframe.getBoundingClientRect();
  const iframeLeftOffset = iframeRect.left;

  const computedStyle = window.getComputedStyle(iframe);
  const marginLeft = computedStyle.marginLeft;

  const iframeWrapper = document.getElementById('iframeWrapper') as HTMLElement;
  const iframeWrapperRect = iframeWrapper.getBoundingClientRect();
  const iframeWrapperLeftOffset = iframeWrapperRect.left;

  let leftOffset = 0;
  if (iframeLeftOffset !== iframeWrapperLeftOffset) {
    leftOffset = iframeLeftOffset - iframeWrapperLeftOffset;
  }

  overlay.style.position = 'absolute';
  overlay.style.top = `${rect.top + window.scrollY}px`;
  overlay.style.left = `${rect.left + leftOffset + window.scrollX}px`;
  overlay.style.width = `${rect.width}px`;
  overlay.style.height = `${rect.height}px`;
  overlay.style.display = 'flex';
};

// reposition the active overlay above the iframe
export const positionSelectedModuleIframeOverlay = (target: HTMLElement | null | undefined) => {
  // this needs to target the selected module inside the iframe
  // while resizing the overlay to match the module modal height and width
  // position the overlay, then adjust the overlay height to match the module modal height
  const iframe = document.getElementById('module-modal') as HTMLElement;

  const activeOverlay = document.getElementById(MODULE_INTERACTION_ACTIVE_ID) as HTMLElement;

  if (target && activeOverlay) {
    const targetRect = target.getBoundingClientRect();
    target.setAttribute(MODULE_INTERACTION_ACTIVE_ATTRIBUTE, 'true');

    positionIframeOverlay(activeOverlay, target);

    // remove 20 pixels from the height to account for additional iframe height
    // used for bottom to display overlays
    const roundedRectangleHeight = 2 * ROUNDED_CORNER_HEIGHT;
    activeOverlay.style.top = `${targetRect.top + ROUNDED_CORNER_HEIGHT}px`;
    activeOverlay.style.height = `${iframe.offsetHeight - MODULE_INTERACTION_ACTIVE_HEIGHT - roundedRectangleHeight}px`;
  }
};

export const clearAllActiveElements = (contentDocument: Document) => {
  const activeElements = contentDocument.querySelectorAll(
    `[${MODULE_INTERACTION_ACTIVE_ATTRIBUTE}="true"], [${MODULE_INTERACTION_HOVER_ATTRIBUTE}="true"]`,
  );

  for (const element of activeElements) {
    element.removeAttribute(MODULE_INTERACTION_ACTIVE_ATTRIBUTE);
    element.removeAttribute(MODULE_INTERACTION_HOVER_ATTRIBUTE);
  }

  const activeOverlay = contentDocument.querySelector(`#${MODULE_INTERACTION_ACTIVE_ID}`) as HTMLElement;
  if (activeOverlay) {
    resetIframeOverlay(activeOverlay);
  }
};

export const elemMouseEnter = (e: MouseEvent, elemId: string, documentElemId: string, isTemplateView: boolean) => {
  if (e && typeof e.stopPropagation === 'function') {
    e.stopPropagation();
  }

  e.preventDefault();

  const currentTarget = e.currentTarget as HTMLElement;

  if (!currentTarget) {
    return;
  }

  const currentTargetAttributes = currentTarget.attributes;

  if (!currentTargetAttributes) {
    return;
  }

  // if the target has an active attribute, do not show the hover state
  if (currentTarget.hasAttribute(MODULE_INTERACTION_ACTIVE_ATTRIBUTE)) {
    return;
  }

  // const textContent = currentTargetAttributes.style?.textContent;
  const textContent = currentTargetAttributes.getNamedItem('style')?.textContent;
  let prevStyleItems = null;
  if (textContent) {
    prevStyleItems = getUniqueStyles(textContent);
  }

  currentTarget.setAttribute(MODULE_INTERACTION_HOVER_ATTRIBUTE, 'true');
  currentTarget.setAttribute(MODULE_INTERACTION_DRAG_ATTRIBUTE, 'true');

  // if (isTemplateView) {
  //   addElemInfo(target, elemId, documentElemId);
  // }

  const { contentDocument } = document.getElementById(documentElemId) as HTMLElementWithDocument;

  // set the hover state
  const hoverOverlay = document.querySelector(`#${MODULE_INTERACTION_HOVER_ID}`) as HTMLElement;
  if (currentTarget && hoverOverlay) {
    positionIframeOverlay(hoverOverlay, currentTarget);
  }

  // if (isTemplateView) {
  //setting elemInfo
  // addTrashCan(target, elemId, documentElemId, curElemWidth);
  // }
};

export const elemMouseLeave = (e: MouseEvent) => {
  //redefine elem styles
  if (e) {
    e.stopPropagation();
  }

  const currentTarget = e.currentTarget as HTMLElement;

  // hide the hover overlay
  // const ownerDocument = currentTarget?.ownerDocument;
  // const hoverOverlay = ownerDocument.querySelector(`#${MODULE_INTERACTION_HOVER_ID}`) as HTMLElement;
  const hoverOverlay = document.getElementById(MODULE_INTERACTION_HOVER_ID) as HTMLElement;

  if (hoverOverlay) {
    resetIframeOverlay(hoverOverlay);
  }

  currentTarget?.removeAttribute(MODULE_INTERACTION_HOVER_ATTRIBUTE);
};

export const elemClick = async (
  e: MouseEvent,
  elemId: string,
  documentElemId: string,
  chosenElemId: string,
  isTemplateView: boolean,
  setChosenElemId: (value: SetStateAction<string>) => void,
  setShowRemoveExperienceModal?: (value: SetStateAction<boolean>) => void,
) => {
  if (e && typeof e.stopPropagation === 'function') {
    e.stopPropagation();
  }
  if (e && typeof e.preventDefault === 'function') {
    e.preventDefault();
  }

  const { contentDocument } = document.getElementById(documentElemId) as HTMLElementWithDocument;

  // clear all elements with active and hovered attributes
  clearAllActiveElements(contentDocument);

  resetPrevElem(elemId, documentElemId, chosenElemId, setChosenElemId);

  const currentTarget = e.currentTarget as HTMLElement;

  // set the active state overlay
  // const activeOverlay = contentDocument.querySelector(`#${MODULE_INTERACTION_ACTIVE_ID}`) as HTMLElement;
  const activeOverlay = document.getElementById(MODULE_INTERACTION_ACTIVE_ID) as HTMLElement;

  if (currentTarget && activeOverlay) {
    currentTarget.setAttribute(MODULE_INTERACTION_ACTIVE_ATTRIBUTE, 'true');

    positionIframeOverlay(activeOverlay, currentTarget);
  }

  // remove the hover state overlay
  const hoverOverlay = document.getElementById(MODULE_INTERACTION_HOVER_ID) as HTMLElement;
  if (hoverOverlay) {
    resetIframeOverlay(hoverOverlay);
  }

  if (currentTarget.hasAttribute(DATA_FILLED_ATTRIBUTE)) {
    // clear the hover attribute for all splitters with DATA_FILLED_ATTRIBUTE except for the clicked one
    const splitters = contentDocument.querySelectorAll(`[${DATA_FILLED_ATTRIBUTE}]`);

    Array.from(splitters).forEach((splitter) => {
      if (splitter !== currentTarget) {
        splitter.removeAttribute(MODULE_INTERACTION_HOVER_ATTRIBUTE);
      }
    });
  }

  //setting current element
  // const textContent = (e.target as HTMLElement)?.getAttribute('style') ?? '';

  // const styleItems = getUniqueStyles(`${textContent} \nborder-color: ${variables.colorV2.module.border.active};`);
  // const styleItems = getUniqueStyles(textContent);

  // (e.target as CurrentTarget)?.setAttribute('style', styleItems);

  await restyleElemInfo(currentTarget, elemId, documentElemId, isTemplateView, setShowRemoveExperienceModal);
};

export const getTemplateDoc = (): Document | null => {
  const node = window.document.querySelector(`#${SAMPLE_TEMPLATE_IFRAME_ID}`);

  return node && (node as HTMLElementWithDocument).contentDocument;
};

export const removeElemInfo = (elemName = '', documentElemId = SAMPLE_TEMPLATE_IFRAME_ID) => {
  // const { contentDocument } = document.getElementById(documentElemId) as HTMLElementWithDocument;
  // const infos = contentDocument.querySelectorAll(`div[id^='elem-info${elemName}']`);
  // const trashCans = contentDocument.querySelectorAll(`div[id^='trash-can${elemName}']`);
  // Array.from(infos).forEach((info) => info.remove());
  // Array.from(trashCans).forEach((trashCan) => trashCan.remove());
};

export const getNewRowsObject = (prevState: Rows, keyToChange: string, experienceId: number | null) =>
  Object.keys(prevState).reduce(
    (acc, splId) => ({
      ...acc,
      [splId]: {
        ...prevState[splId],
      },
      [keyToChange]: {
        experienceId,
      },
    }),
    {},
  );

export const getElementTopPosition = (elemId: string): number => {
  const { contentDocument } = document.getElementById(SAMPLE_TEMPLATE_IFRAME_ID) as HTMLElementWithDocument;
  const currentElem = contentDocument.getElementById(elemId);
  const elementTopPosition = currentElem?.getBoundingClientRect().top ?? 0 - 180;
  return elementTopPosition;
};

export const scrollToModule = (
  sampleTemplateRef: React.RefObject<HTMLDivElement>,
  elementTopPosition: number,
): void => {
  const scrollingArea = sampleTemplateRef.current;

  if (scrollingArea) {
    scrollingArea.scrollTo(0, elementTopPosition);
    scrollingArea.style.overflow = 'hidden';
  }
};
